import { useEffect, forwardRef, useImperativeHandle, useRef, useCallback, useState } from 'react';
import { KeyframeContainer } from './KeyframeContainer';
import type { Keyframe, KeyframeManagerProps } from '../types';
import { roundTime } from '../utils';
import { selectKeyframesByAssetId, selectKeyframeById } from '@/features/keyframes/keyframesSlice';
import { updateKeyframeThunk } from '@/features/keyframes/keyframesThunks';
import store from '@/store/store';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { setDragOperation } from '@/pages/editor/components/timeline/TimelineCategoryItem/keyframeSelectionHelpers';

import type { WriteScope } from '@/modules/undoRedo';
export const KeyframeManager = forwardRef<
  {
    handleAssetMove: (
      newStartTime: number,
      newEndTime: number,
      oldStartTime: number,
      oldEndTime: number,
      scope?: WriteScope
    ) => Promise<void>;
    keyframes?: Keyframe[];
    addKeyframe?: (assetId: string, relativeTime: number) => Promise<Keyframe | null>;
    updateKeyframeProperties?: (keyframeId: string, properties: Partial<Keyframe>) => Promise<void>;
    selectKeyframe?: (keyframeId: string | any) => Promise<void>;
    deleteKeyframe?: (keyframeId: string) => Promise<void>;
    resetKeyframeSelection?: () => void;
    getDragPositions?: () => Record<string, number>;
  },
  KeyframeManagerProps
>(
  (
    {
      sceneInstanceId,
      assetInstanceId,
      asset,
      assetCategory,
      scale,
      isLocked,
      isVisible,
      selectedKeyframeId,
      keyframes = [],
      itemHeight,
      onKeyframeSelect,
      onLoadKeyframes,
      onAddKeyframe,
      onUpdateKeyframe,
      onDeleteKeyframe,
      onSelectKeyframe,
      environmentPreferencesKeyframesOn = false,
      resizeOffset,
      onDragPositionsChange,
      isPlaying = false
    },
    ref
  ) => {
    const services = useServices();
    const [dragPositions, setDragPositions] = useState<Record<string, number>>({});

    // Sync drag positions to parent via effect to avoid setState-during-render warning
    useEffect(() => {
      onDragPositionsChange?.(dragPositions);
    }, [dragPositions, onDragPositionsChange]);

    // Track previous selection to avoid sending duplicate actions
    const prevSelectedKeyframeRef = useRef<string | null>(null);

    // Load keyframes on mount
    useEffect(() => {
      if (environmentPreferencesKeyframesOn && assetInstanceId && onLoadKeyframes) {
        onLoadKeyframes(assetInstanceId);
      }
    }, [assetInstanceId, onLoadKeyframes, environmentPreferencesKeyframesOn]);

    // Edit mode tracking removed - keyframes now always interactive

  // Select keyframe - 'timeline' source means it was clicked directly on a keyframe marker
  const handleKeyframeSelect = useCallback((keyframeId: string | null) => {
    // IMPORTANT: Read the current selection BEFORE updating Redux, so we have the actual previous selection
    // Find previous keyframe from Redux (can be from any asset, not just current one)
    const currentState = store.getState();
    const reduxSelectedKeyframeId = currentState.editorSelection.selectedKeyframeId;
    const previousKeyframe = reduxSelectedKeyframeId 
      ? selectKeyframeById(currentState, reduxSelectedKeyframeId) || null 
      : null;
    
    // Pass 'timeline' as source since this is called from clicking on a keyframe marker
    // This updates Redux, so we must read selectedKeyframeId BEFORE this call
    onKeyframeSelect?.(keyframeId, assetInstanceId, 'timeline');

    // Always send selection to Unity (removed edit mode check)
    if (keyframeId) {
      const selectedKeyframe = keyframes.find(kf => kf.id === keyframeId);
      if (selectedKeyframe) {
        onSelectKeyframe?.(selectedKeyframe, previousKeyframe, sceneInstanceId);
      }
    }
  }, [keyframes, sceneInstanceId, assetInstanceId, onKeyframeSelect, onSelectKeyframe]);

    // Add keyframe - EXACTLY matching context menu logic
    const handleKeyframeAdd = useCallback(async (_assetInstanceId: string, timePosition: number): Promise<Keyframe | null> => {
      const roundedRelativeTime = roundTime(timePosition);
      const keyframeAbsoluteTime = asset.startTime + roundedRelativeTime;
      
      // Reload keyframes from Supabase to get latest positions
      await onLoadKeyframes?.(assetInstanceId);
      
      // Wait for Redux state to update
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Get fresh keyframes from Redux store (not the prop, which is stale due to closure)
      const freshKeyframes = selectKeyframesByAssetId(store.getState(), assetInstanceId);
      
      // Find previous keyframe to inherit values (matches context menu)
      const sortedKeyframes = [...freshKeyframes].sort((a, b) => a.timePosition - b.timePosition);
      const previousKeyframe = sortedKeyframes
        .filter(kf => kf.timePosition < keyframeAbsoluteTime)
        .pop();

      const inheritedValues = previousKeyframe
        ? {
            position: previousKeyframe.position,
            rotation: previousKeyframe.rotation,
            scale: previousKeyframe.scale
          }
        : {
            position: { x: 0, y: 0, z: 0 },
            rotation: { x: 0, y: 0, z: 0 },
            scale: { x: 1, y: 1, z: 1 }
          };

      // Determine if this keyframe should be categorized as 'end' (EXACT context menu logic)
      const existingKeyframes = [...freshKeyframes];
      const latestExistingKeyframe = existingKeyframes.reduce((latest, current) => 
        current.timePosition > latest.timePosition ? current : latest, 
        existingKeyframes[0] || { timePosition: -1 }
      );
      const isLastKeyframe = keyframeAbsoluteTime > latestExistingKeyframe.timePosition;
      
      // Unity now handles promotion/demotion of 'end' keyframes automatically
      // No need to manually update old 'end' keyframe to 'middle' here
      
      // Generate proper UUID for the keyframe
      const keyframeId = crypto.randomUUID();
      const newKeyframe: Keyframe = {
        id: keyframeId,
        assetInstanceId,
        timePosition: keyframeAbsoluteTime,
        category: isLastKeyframe ? 'end' : 'middle',
        sceneInstanceId: sceneInstanceId,
        ...inheritedValues
      };

      // Add to database and Redux (the keyframe service tells the renderer)
      await onAddKeyframe?.(newKeyframe);
      
      // Reload keyframes to update the component with the newly created keyframe
      // This ensures the marker appears immediately on the timeline
      await onLoadKeyframes?.(assetInstanceId);
      
      // Wait longer to ensure Unity receives and processes creation event before selection
      // Supabase broadcast + Unity processing can take 100-200ms
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Select the newly created keyframe
      onKeyframeSelect?.(keyframeId, assetInstanceId);
      // When adding a new keyframe, there's no previous selection
      onSelectKeyframe?.(newKeyframe, null, sceneInstanceId);

      return newKeyframe;
    }, [assetInstanceId, asset.startTime, sceneInstanceId, onAddKeyframe, onKeyframeSelect, onSelectKeyframe, onLoadKeyframes]);

    // Move keyframe (during drag)
    const handleKeyframeMove = useCallback((keyframeId: string, newRelativeTime: number) => {
      const newAbsoluteTime = asset.startTime + newRelativeTime;
      setDragPositions(prev => ({ ...prev, [keyframeId]: newAbsoluteTime }));
    }, [asset.startTime]);

    // Finalize keyframe move (on drag end)
    const handleKeyframeMoveEnd = useCallback(async (keyframeId: string) => {
      const newAbsoluteTime = dragPositions[keyframeId];
      if (newAbsoluteTime === undefined) return;

      const keyframe = keyframes.find(kf => kf.id === keyframeId);
      if (!keyframe) return;

      // Create updated keyframe with new time
      const updated: Keyframe = { ...keyframe, timePosition: newAbsoluteTime };

      // Mark this as a drag operation to prevent a duplicate selection from sendEditorKeyframeSelected
      setDragOperation(keyframeId);

      // Shown now and saved, put back if the save fails; the keyframe service tells the renderer and records the undo step
      if (services?.keyframe) {
        store.dispatch(updateKeyframeThunk(services, updated)).catch(err => {
          console.error('[KeyframeManager] Failed to update keyframe time:', err);
        });
      }

      setDragPositions(prev => {
        const rest = { ...prev };
        delete rest[keyframeId];
        return rest;
      });
    }, [dragPositions, keyframes, services]);

    // Delete keyframe
    const handleKeyframeDelete = useCallback(async (keyframeId: string) => {
      const keyframeToDelete = keyframes.find(kf => kf.id === keyframeId);
      if (!keyframeToDelete) return;

      // Prevent deletion of start keyframes
      if (keyframeToDelete.category === 'start') {
        return;
      }

      // Delete from database (the service layer tells the renderer)
      onDeleteKeyframe?.(keyframeId);

      // Unity now handles promotion/demotion of 'end' keyframes automatically
      // No need to manually promote the new last keyframe here
    }, [keyframes, onDeleteKeyframe]);

    // Handle asset move - update all keyframes
    // DRAG: All keyframes move by the same offset (duration stays same)
    // LEFT RESIZE: Only start keyframe moves to newStartTime, others stay in place
    // RIGHT RESIZE: Only end keyframe moves to newEndTime, others stay in place
    const handleAssetMove = useCallback(async (
      newStartTime: number,
      newEndTime: number,
      oldStartTime: number,
      oldEndTime: number,
      scope?: WriteScope
    ) => {
      const startChanged = Math.abs(newStartTime - oldStartTime) > 0.001;
      const endChanged = Math.abs(newEndTime - oldEndTime) > 0.001;
      const oldDuration = oldEndTime - oldStartTime;
      const newDuration = newEndTime - newStartTime;
      const durationChanged = Math.abs(newDuration - oldDuration) > 0.001;
      
      // Determine operation type
      const isDrag = startChanged && endChanged && !durationChanged;
      const isLeftResize = startChanged && !endChanged && durationChanged;
      const isRightResize = !startChanged && endChanged && durationChanged;
      
      const timeOffset = newStartTime - oldStartTime;
      
      if (keyframes.length === 0) {
        return;
      }

      const updatePromises = keyframes.map(async (keyframe) => {
        let newAbsoluteTime: number;

        if (isDrag) {
          // DRAG: All keyframes move by the same offset
          newAbsoluteTime = keyframe.timePosition + timeOffset;
        } else if (isLeftResize) {
          // LEFT RESIZE: Start keyframe only moves when shrinking PAST it
          // If the new start time is still before the KF position, don't move it
          if (keyframe.category === 'start' && newStartTime > keyframe.timePosition) {
            newAbsoluteTime = newStartTime;
          } else {
            newAbsoluteTime = keyframe.timePosition; // Stay in place
          }
        } else if (isRightResize) {
          // RIGHT RESIZE: End keyframe NEVER moves - it's a stop constraint
          // All keyframes stay in place during right resize
          newAbsoluteTime = keyframe.timePosition;
        } else {
          // Unknown operation - default to moving by offset
          newAbsoluteTime = keyframe.timePosition + timeOffset;
        }

        // Only update if position actually changed
        if (Math.abs(newAbsoluteTime - keyframe.timePosition) > 0.001) {
          const updated: Keyframe = { ...keyframe, timePosition: newAbsoluteTime };

          // Shown now and saved as part of the move's undo step, put back if the save fails; the keyframe service tells the renderer
          if (services?.keyframe) {
            await store.dispatch(updateKeyframeThunk(services, updated, scope)).catch(err => {
              console.error('[KeyframeManager] Failed to update keyframe time on asset drag:', err);
            });
          }
        }
      });

      await Promise.all(updatePromises);
    }, [keyframes, services]);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      handleAssetMove,
      keyframes,
      addKeyframe: handleKeyframeAdd,
      updateKeyframeProperties: async (keyframeId: string, properties: Partial<Keyframe>) => {
        const keyframe = keyframes.find(kf => kf.id === keyframeId);
        if (!keyframe) return;
        const updated = { ...keyframe, ...properties } as Keyframe;
        onUpdateKeyframe?.(updated);
      },
      selectKeyframe: async (keyframeId: string) => {
        // IMPORTANT: Read previous keyframe BEFORE updating Redux
        const currentState = store.getState();
        const reduxSelectedKeyframeId = currentState.editorSelection.selectedKeyframeId;
        const previousKeyframe = reduxSelectedKeyframeId 
          ? selectKeyframeById(currentState, reduxSelectedKeyframeId) || null 
          : null;
        
        // Update React state (this updates Redux)
        onKeyframeSelect?.(keyframeId, assetInstanceId);
        
        // Select it in the renderer
        const selectedKeyframe = keyframes.find(kf => kf.id === keyframeId);
        if (selectedKeyframe) {
          onSelectKeyframe?.(selectedKeyframe, previousKeyframe, sceneInstanceId);
        }
      },
      deleteKeyframe: handleKeyframeDelete,
      resetKeyframeSelection: () => {
        prevSelectedKeyframeRef.current = null;
      },
      getDragPositions: () => dragPositions
    }), [handleAssetMove, keyframes, handleKeyframeAdd, handleKeyframeDelete, sceneInstanceId, assetInstanceId, onKeyframeSelect, onUpdateKeyframe, onSelectKeyframe, dragPositions]);

    // Apply drag positions to keyframes for visual feedback
    // Also filter out any invalid keyframes to prevent crashes
    // During resize, adjust start/end keyframe positions to move with the edges
    const effectiveKeyframes = (keyframes || [])
      .filter(kf => kf && kf.id && kf.timePosition !== undefined && kf.timePosition !== null)
      .map(kf => {
        // If being dragged, use drag position
        if (dragPositions[kf.id] !== undefined) {
          return { ...kf, timePosition: dragPositions[kf.id] };
        }
        
        // During left resize, start keyframe moves with the edge when shrinking PAST it
        // During right resize, end keyframe does NOT move - it acts as a stop constraint
        if (resizeOffset && resizeOffset.side === 'left') {
          const newStartTime = asset.startTime + resizeOffset.deltaTime;
          
          if (kf.category === 'start' && newStartTime > kf.timePosition) {
            // Start keyframe moves with the left edge when shrinking PAST it
            return { ...kf, timePosition: newStartTime };
          }
        }
        // Note: End keyframe stays put during right resize - it's a constraint, not movable
        
        return kf;
      });

    return (
      <KeyframeContainer
        assetInstanceId={assetInstanceId}
        assetStartTime={asset.startTime}
        assetDuration={asset.duration || 0}
        assetCategory={assetCategory}
        scale={scale}
        isLocked={isLocked}
        isVisible={isVisible}
        keyframes={effectiveKeyframes}
        itemHeight={itemHeight}
        onKeyframeSelect={handleKeyframeSelect}
        selectedKeyframeId={selectedKeyframeId}
        onKeyframeAdd={handleKeyframeAdd}
        onKeyframeMove={handleKeyframeMove}
        onKeyframeMoveEnd={handleKeyframeMoveEnd}
        resizeOffset={resizeOffset}
        isPlaying={isPlaying}
      />
    );
  }
);

KeyframeManager.displayName = 'KeyframeManager';
